import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Save, Upload, Plus, Trash2, Home, Info, Briefcase, Phone, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContentPage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('site_info');
  
  // Content states
  const [siteInfo, setSiteInfo] = useState({
    name: '',
    subtitle: '',
    tagline: '',
    phone: '',
    email: '',
    instagram: ''
  });
  
  const [hero, setHero] = useState({
    images: []
  });
  
  const [about, setAbout] = useState({
    title: '',
    subtitle: '',
    paragraphs: [],
    image: ''
  });
  
  const [services, setServices] = useState([]);
  
  const [contact, setContact] = useState({
    phone: '',
    email: '',
    instagram: '',
    address: '',
    hours: ''
  });

  const fileInputRef = useRef(null);
  const heroFileInputRef = useRef(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      const data = response.data;
      
      if (data.site_info) setSiteInfo(data.site_info);
      if (data.hero) setHero(data.hero);
      if (data.about) setAbout(data.about);
      if (data.services) setServices(data.services);
      if (data.contact) setContact(data.contact);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await axios.post(
          `${API}/upload/base64`,
          { image: reader.result },
          { headers: getAuthHeaders() }
        );
        callback(`${BACKEND_URL}${response.data.url}`);
        toast.success('Image uploadée');
      } catch (error) {
        toast.error("Erreur lors de l'upload");
      }
    };
    reader.readAsDataURL(file);
  };

  const saveSiteInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/site_info`, siteInfo, {
        headers: getAuthHeaders()
      });
      toast.success('Informations du site mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveHero = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/hero`, hero, {
        headers: getAuthHeaders()
      });
      toast.success('Section accueil mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveAbout = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/about`, about, {
        headers: getAuthHeaders()
      });
      toast.success('Page À propos mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveServices = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/services`, services, {
        headers: getAuthHeaders()
      });
      toast.success('Services mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/contact`, contact, {
        headers: getAuthHeaders()
      });
      toast.success('Informations de contact mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addParagraph = () => {
    setAbout({ ...about, paragraphs: [...about.paragraphs, ''] });
  };

  const updateParagraph = (index, value) => {
    const newParagraphs = [...about.paragraphs];
    newParagraphs[index] = value;
    setAbout({ ...about, paragraphs: newParagraphs });
  };

  const removeParagraph = (index) => {
    const newParagraphs = about.paragraphs.filter((_, i) => i !== index);
    setAbout({ ...about, paragraphs: newParagraphs });
  };

  const addService = () => {
    setServices([...services, {
      id: services.length + 1,
      title: '',
      description: '',
      icon: 'Flower2',
      features: []
    }]);
  };

  const updateService = (index, field, value) => {
    const newServices = [...services];
    newServices[index] = { ...newServices[index], [field]: value };
    setServices(newServices);
  };

  const removeService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const iconOptions = [
    { value: 'Flower2', label: '🌸 Fleur' },
    { value: 'Sparkles', label: '✨ Étoiles' },
    { value: 'Heart', label: '❤️ Cœur' },
    { value: 'Globe', label: '🌍 Globe' },
    { value: 'Gift', label: '🎁 Cadeau' },
    { value: 'Calendar', label: '📅 Calendrier' },
    { value: 'Camera', label: '📷 Photo' },
    { value: 'Star', label: '⭐ Étoile' }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#3D2E35]">Contenu du site</h1>
            <p className="text-[#6B5A62] mt-1">Modifiez le contenu de vos pages</p>
          </div>
          <Link
            to="/admin/creations"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B4B7C] text-white rounded-xl hover:bg-[#C45B9F] transition-colors"
          >
            <ImageIcon size={18} />
            <span>Gérer les Créations</span>
            <ExternalLink size={16} />
          </Link>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white flex-wrap h-auto gap-2 p-2">
            <TabsTrigger value="site_info" className="flex items-center gap-2">
              <Home size={16} /> Général
            </TabsTrigger>
            <TabsTrigger value="hero" className="flex items-center gap-2">
              <ImageIcon size={16} /> Accueil
            </TabsTrigger>
            <TabsTrigger value="about" className="flex items-center gap-2">
              <Info size={16} /> À propos
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Briefcase size={16} /> Services
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Phone size={16} /> Contact
            </TabsTrigger>
          </TabsList>

          {/* Site Info Tab */}
          <TabsContent value="site_info" className="mt-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="font-heading text-xl text-[#3D2E35]">Informations générales</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nom du site</Label>
                  <Input
                    value={siteInfo.name}
                    onChange={(e) => setSiteInfo({ ...siteInfo, name: e.target.value })}
                    placeholder="Tevaite"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sous-titre</Label>
                  <Input
                    value={siteInfo.subtitle}
                    onChange={(e) => setSiteInfo({ ...siteInfo, subtitle: e.target.value })}
                    placeholder="Créations Florales"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Slogan / Tagline</Label>
                <Input
                  value={siteInfo.tagline}
                  onChange={(e) => setSiteInfo({ ...siteInfo, tagline: e.target.value })}
                  placeholder="L'art floral tropical au cœur de l'émotion"
                  className="border-[#E8A5C4]/30"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={siteInfo.phone}
                    onChange={(e) => setSiteInfo({ ...siteInfo, phone: e.target.value })}
                    placeholder="+689 89 29 29 30"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={siteInfo.email}
                    onChange={(e) => setSiteInfo({ ...siteInfo, email: e.target.value })}
                    placeholder="contact@tevaite-creation.com"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={siteInfo.instagram}
                    onChange={(e) => setSiteInfo({ ...siteInfo, instagram: e.target.value })}
                    placeholder="@tevaite_creation"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              <Button onClick={saveSiteInfo} disabled={saving} className="btn-tevaite text-white">
                <Save size={18} className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </TabsContent>

          {/* Hero Tab */}
          <TabsContent value="hero" className="mt-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="font-heading text-xl text-[#3D2E35]">Page d'accueil - Image principale</h2>
              
              <div className="space-y-4">
                <Label>Image hero</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  {hero.images && hero.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`Hero ${index + 1}`}
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <button
                        onClick={() => setHero({ ...hero, images: hero.images.filter((_, i) => i !== index) })}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <div
                    onClick={() => heroFileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#E8A5C4]/50 rounded-xl p-8 text-center cursor-pointer hover:border-[#C45B9F] transition-colors h-48 flex flex-col items-center justify-center"
                  >
                    <Upload className="text-[#C45B9F] mb-2" size={32} />
                    <p className="text-[#6B5A62]">Ajouter une image</p>
                  </div>
                </div>
                <input
                  ref={heroFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, (url) => setHero({ ...hero, images: [...(hero.images || []), url] }))}
                  className="hidden"
                />
              </div>

              <Button onClick={saveHero} disabled={saving} className="btn-tevaite text-white">
                <Save size={18} className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about" className="mt-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="font-heading text-xl text-[#3D2E35]">Page À propos</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input
                    value={about.title}
                    onChange={(e) => setAbout({ ...about, title: e.target.value })}
                    placeholder="Notre Histoire"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sous-titre</Label>
                  <Input
                    value={about.subtitle}
                    onChange={(e) => setAbout({ ...about, subtitle: e.target.value })}
                    placeholder="Une passion florale née au cœur du Pacifique"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Paragraphes</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addParagraph}>
                    <Plus size={16} className="mr-1" /> Ajouter
                  </Button>
                </div>
                {about.paragraphs && about.paragraphs.map((p, index) => (
                  <div key={index} className="flex gap-2">
                    <Textarea
                      value={p}
                      onChange={(e) => updateParagraph(index, e.target.value)}
                      placeholder={`Paragraphe ${index + 1}`}
                      rows={3}
                      className="flex-1 border-[#E8A5C4]/30"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeParagraph(index)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <Label>Photo</Label>
                <div className="flex items-start gap-4">
                  {about.image && (
                    <img
                      src={about.image}
                      alt="À propos"
                      className="w-48 h-48 object-cover rounded-xl"
                    />
                  )}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#E8A5C4]/50 rounded-xl p-6 text-center cursor-pointer hover:border-[#C45B9F] transition-colors"
                  >
                    <Upload className="text-[#C45B9F] mx-auto mb-2" size={24} />
                    <p className="text-[#6B5A62] text-sm">Changer l'image</p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, (url) => setAbout({ ...about, image: url }))}
                  className="hidden"
                />
              </div>

              <Button onClick={saveAbout} disabled={saving} className="btn-tevaite text-white">
                <Save size={18} className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="mt-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl text-[#3D2E35]">Services</h2>
                <Button type="button" variant="outline" onClick={addService}>
                  <Plus size={16} className="mr-1" /> Ajouter un service
                </Button>
              </div>
              
              <div className="space-y-6">
                {services.map((service, index) => (
                  <div key={index} className="border border-[#E8A5C4]/30 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#8B4B7C]">Service {index + 1}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeService(index)}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Titre</Label>
                        <Input
                          value={service.title}
                          onChange={(e) => updateService(index, 'title', e.target.value)}
                          placeholder="Nom du service"
                          className="border-[#E8A5C4]/30"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Icône</Label>
                        <Select
                          value={service.icon}
                          onValueChange={(value) => updateService(index, 'icon', value)}
                        >
                          <SelectTrigger className="border-[#E8A5C4]/30">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {iconOptions.map((icon) => (
                              <SelectItem key={icon.value} value={icon.value}>
                                {icon.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={service.description}
                        onChange={(e) => updateService(index, 'description', e.target.value)}
                        placeholder="Description du service"
                        rows={2}
                        className="border-[#E8A5C4]/30"
                      />
                    </div>

                    {/* Image du service */}
                    <div className="space-y-2">
                      <Label>Image du service</Label>
                      <div className="flex items-start gap-4">
                        {service.image ? (
                          <div className="relative group">
                            <img
                              src={service.image}
                              alt={service.title}
                              className="w-32 h-32 object-cover rounded-xl"
                            />
                            <button
                              type="button"
                              onClick={() => updateService(index, 'image', '')}
                              className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 border-2 border-dashed border-[#E8A5C4]/50 rounded-xl flex items-center justify-center">
                            <ImageIcon className="text-[#C45B9F]/50" size={24} />
                          </div>
                        )}
                        <div className="flex-1">
                          <Input
                            value={service.image || ''}
                            onChange={(e) => updateService(index, 'image', e.target.value)}
                            placeholder="URL de l'image ou uploadez ci-dessous"
                            className="border-[#E8A5C4]/30 mb-2"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            id={`service-image-${index}`}
                            onChange={(e) => handleImageUpload(e, (url) => updateService(index, 'image', url))}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById(`service-image-${index}`).click()}
                          >
                            <Upload size={14} className="mr-1" /> Uploader une image
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={saveServices} disabled={saving} className="btn-tevaite text-white">
                <Save size={18} className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="mt-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="font-heading text-xl text-[#3D2E35]">Page Contact</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    placeholder="+689 89 29 29 30"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="contact@tevaite-creation.com"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={contact.instagram}
                    onChange={(e) => setContact({ ...contact, instagram: e.target.value })}
                    placeholder="@tevaite_creation"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse / Localisation</Label>
                  <Input
                    value={contact.address}
                    onChange={(e) => setContact({ ...contact, address: e.target.value })}
                    placeholder="Polynésie française"
                    className="border-[#E8A5C4]/30"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Horaires</Label>
                <Textarea
                  value={contact.hours}
                  onChange={(e) => setContact({ ...contact, hours: e.target.value })}
                  placeholder="Lundi - Vendredi : 8h - 18h..."
                  rows={4}
                  className="border-[#E8A5C4]/30"
                />
              </div>

              <Button onClick={saveContact} disabled={saving} className="btn-tevaite text-white">
                <Save size={18} className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminContentPage;
